import React from 'react';

import LoadingButton, { type LoadingButtonProps } from '@mui/lab/LoadingButton';
import DialogActions, { type DialogActionsProps } from '@mui/material/DialogActions';
import Divider from '@mui/material/Divider';
import Stack from '@mui/material/Stack';
import { useTheme } from '@mui/material/styles';

import { ConnectionStatusFooter } from 'components/ConnectionStatus';
import type { TooltipButtonProps } from 'components/QButton/TooltipSubmitButton';
import TooltipSubmitButton from 'components/QButton/TooltipSubmitButton';

type StdDialogButtonProps = LoadingButtonProps;

type StdDialogActionProps = {
  divider?: boolean;
  sx?: DialogActionsProps['sx'];
  primary: TooltipButtonProps & {
    children?: any;
    id?: string;
    onClick?: (e?: any) => void;
    type?: string;
    variant?: string;
  };
  secondary?: StdDialogButtonProps & {
    children?: any;
    id?: string;
    onClick?: (e?: any) => void;
    variant?: string;
  };
  secondaryNode?: any;
  tertiaryAction?: StdDialogButtonProps & {
    children?: any;
    onClick?: (e?: any) => void;
  };
  tertiaryNode?: any;
  colorBar?: string;
  /** While offline, show the connection banner here: `replace` hides these actions, `above` keeps them usable. */
  connectionBanner?: 'replace' | 'above';
};

const StdDialogActions = (props: StdDialogActionProps) => {
  const theme = useTheme();

  const {
    divider,
    sx,
    primary: {
      children: primaryChildren = 'Confirm',
      id: primaryId = 'primary-button',
      onClick: primaryOnClick,
      type: primaryType = 'submit',
      variant: primaryVariant = 'contained',
      showTooltip,
      tooltipText,
      loading: primaryLoading,
      disabled: primaryDisabled,
      ...primaryOther
    } = {},
    secondary: {
      children: secondaryChildren = 'Cancel',
      id: secondaryId = 'secondary-button',
      onClick: secondaryOnClick,
      variant: secondaryVariant = 'outlined',
      loading: secondaryLoading,
      disabled: secondaryDisabled,
      ...secondaryOther
    } = {},
    secondaryNode,
    tertiaryAction: {
      children: tertiaryActionChildren,
      onClick: tertiaryActionOnClick,
      loading: tertiaryLoading,
      disabled: tertiaryDisabled,
      ...tertiaryActionOther
    } = {},
    tertiaryNode,
    colorBar = theme.palette.background.paper,
    connectionBanner,
  } = props;

  // will inject standard error tooltip if showTooltip/tooltipText provided
  const doTooltip = Boolean(showTooltip || tooltipText);
  const PrimaryButton = doTooltip ? TooltipSubmitButton : LoadingButton;
  const tooltipProps = doTooltip ? { tooltipText, showTooltip } : {};

  //  Set Z-Index to 1 instead of 0 so it is above TextField labels; the connection footer is sticky itself.
  const actions = (
    <Stack
      sx={{
        ...(connectionBanner ? {} : { position: 'sticky', bottom: '0', zIndex: 1 }),
        backgroundColor: colorBar,
      }}
    >
      {divider && <Divider />}
      <DialogActions sx={sx}>
        <Stack direction='row' flex='1' alignItems='center' justifyContent='space-between'>
          {tertiaryActionChildren && tertiaryActionOnClick && (
            <LoadingButton
              onClick={tertiaryActionOnClick}
              disabled={tertiaryDisabled}
              loading={Boolean(tertiaryLoading)}
              {...tertiaryActionOther}
            >
              {tertiaryActionChildren}
            </LoadingButton>
          )}
          {tertiaryNode}
          <Stack direction='row' gap={2} sx={{ marginLeft: 'auto' }}>
            {secondaryChildren && secondaryOnClick && (
              <LoadingButton
                id={secondaryId}
                onClick={secondaryOnClick}
                variant={secondaryVariant}
                disabled={secondaryDisabled}
                loading={Boolean(secondaryLoading)}
                {...secondaryOther}
              >
                {secondaryChildren}
              </LoadingButton>
            )}
            {secondaryNode}
            {(primaryType === 'submit' || primaryOnClick) && (
              <PrimaryButton
                id={primaryId}
                onClick={primaryOnClick}
                type={primaryType}
                variant={primaryVariant}
                disabled={primaryDisabled}
                loading={Boolean(primaryLoading)}
                {...tooltipProps}
                {...primaryOther}
              >
                {primaryChildren}
              </PrimaryButton>
            )}
          </Stack>
        </Stack>
      </DialogActions>
    </Stack>
  );

  if (!connectionBanner) return actions;
  return <ConnectionStatusFooter keepActions={connectionBanner === 'above'}>{actions}</ConnectionStatusFooter>;
};

StdDialogActions.displayName = 'StdDialogActions';

export default StdDialogActions;
